import { createElement, useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { Button } from './Button';

type DialogProps = {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
};

export function Dialog({ open, title, onClose, children, footer }: DialogProps) {
  useEffect(() => {
    if (!open) {
      return;
    }
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        onClose();
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    createElement(
      'div',
      {
        className: 'cs-dialog-scrim',
        'data-cs-dialog': 'true',
        onMouseDown: (e: MouseEvent) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        },
      },
      createElement(
        'div',
        {
          className: 'cs-dialog-panel',
          onMouseDown: (e: MouseEvent) => e.stopPropagation(),
        },
        createElement(
          'div',
          { className: 'cs-dialog-header' },
          createElement('h2', { className: 'cs-text-h3' }, title),
        ),
        createElement('div', { className: 'cs-dialog-body' }, children),
        footer ? createElement('div', { className: 'cs-dialog-footer' }, footer) : null,
      ),
    ),
    document.body,
  );
}

export function DialogButton({
  label,
  onPress,
  variant = 'secondary',
  disabled,
}: {
  label: string;
  onPress: () => void;
  variant?: 'primary' | 'secondary' | 'danger';
  disabled?: boolean;
}) {
  const mapped =
    variant === 'primary' ? 'primary' : variant === 'danger' ? 'danger' : 'secondary';

  return createElement(Button, {
    label,
    onPress,
    variant: mapped,
    size: 'sm',
    disabled,
    className: 'self-auto',
  });
}
